import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
    Grid,
    Button,
    createTheme,
    Dialog,
    DialogActions,
    DialogTitle,
    Typography,
    TextField,
    InputAdornment,
} from '@material-ui/core';
import { ModalContext } from '../../context';
import { useClientUser } from '@/hooks';
import { css } from '@emotion/react';
import { OnwardCheckbox } from '../../../../blocks';
import { SectionSubtitle2 } from '@/styles/blocks';
import SearchIcon from '@material-ui/icons/Search';
import { DOCUMENTS_BY_CLIENT_ID } from '@/components/Account/CustomDocuments/graphql';
import { useLazyQuery } from '@apollo/client';

/**
 * Searchable checklist of the account's custom documents to attach to the order in the Edit Order
 * modal. Takes no props; reads the order and `modifyOrder` from `ModalContext`.
 */
function AdditionalDocuments() {
    const { state: modalState, callbacks } = useContext(ModalContext);
    const [search, setSearch] = useState('');
    const [fetchedDocuments, setFetchedDocuments] = useState([]);

    const order = modalState?.order;

    const { documents, isOnwardAdmin, isImposter } = useClientUser();

    const [getCustomDocumentsByClientId] = useLazyQuery(DOCUMENTS_BY_CLIENT_ID);

    useEffect(() => {
        (async () => {
            if (isOnwardAdmin && !isImposter) {
                // fetch the shipper's documents
                const docsResponse = await getCustomDocumentsByClientId({
                    variables: { client_id: order?.shipper_id },
                });
                setFetchedDocuments(docsResponse?.data?.documents || []);
            }
        })();
    }, [isOnwardAdmin, isImposter, order]);

    let shipperDocuments = useMemo(() => {
        if (fetchedDocuments?.length) {
            return fetchedDocuments;
        }
        return documents;
    }, [documents, fetchedDocuments]);

    const searchedDocuments = useMemo(() => {
        const lowerCaseSearch = search.toLowerCase();
        return shipperDocuments.filter(
            (doc) =>
                doc.name.toLowerCase().includes(lowerCaseSearch) ||
                doc.type.toLowerCase().includes(lowerCaseSearch) ||
                order?.documents?.find((d) => d.document_id === doc.document_id)
        );
    }, [shipperDocuments, search, order]);

    const allChecked = useMemo(() => {
        return (
            searchedDocuments.length > 0 &&
            searchedDocuments.every((doc) => (order?.documents || []).some((d) => d.document_id === doc.document_id))
        );
    }, [searchedDocuments, order]);

    if (!shipperDocuments?.length) {
        return <></>;
    }

    return (
        <>
            <Grid
                container
                direction="column"
                css={css`
                    flex-wrap: nowrap;
                `}
            >
                <Grid item>
                    <SectionSubtitle2>Attach SOP / Assembly Instructions</SectionSubtitle2>
                </Grid>
                <Grid
                    item
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="Search..."
                        value={search || ''}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        css={css`
                            background-color: white;
                            width: 100%;
                            margin-top: 0.2rem;
                        `}
                    />
                </Grid>
                <hr
                    css={css`
                        margin: 0;
                    `}
                />
                <Grid
                    container
                    css={css`
                        padding: 14px;
                        align-items: center;
                    `}
                >
                    <Grid item xs={1}>
                        <OnwardCheckbox
                            checked={allChecked}
                            onChange={() => {
                                const attached = order?.documents || [];

                                if (allChecked) {
                                    const searchedIds = new Set(searchedDocuments.map((doc) => doc.document_id));
                                    callbacks.modifyOrder({
                                        documents: attached.filter((d) => !searchedIds.has(d.document_id)),
                                    });
                                } else {
                                    const attachedIds = new Set(attached.map((d) => d.document_id));
                                    callbacks.modifyOrder({
                                        documents: [
                                            ...attached,
                                            ...searchedDocuments
                                                .filter((doc) => !attachedIds.has(doc.document_id))
                                                .map((doc) => ({
                                                    document_id: doc.document_id,
                                                    ...(order.order_id ? { order_id: order.order_id } : {}),
                                                })),
                                        ],
                                    });
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <Typography
                            css={css`
                                font-weight: 700;
                            `}
                        >
                            Name
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography
                            css={css`
                                font-weight: 700;
                            `}
                        >
                            Document Type
                        </Typography>
                    </Grid>
                </Grid>

                {searchedDocuments.map((doc) => (
                    <>
                        <hr
                            css={css`
                                margin: 0;
                            `}
                        />

                        <Grid
                            container
                            css={css`
                                padding: 14px;
                                align-items: center;
                            `}
                        >
                            <Grid item xs={1}>
                                <OnwardCheckbox
                                    checked={order?.documents?.find((d) => d.document_id === doc.document_id) || false}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const newDocs = [
                                                ...(order?.documents?.length ? order.documents : []),
                                                {
                                                    document_id: doc.document_id,
                                                    ...(order.order_id ? { order_id: order.order_id } : {}),
                                                },
                                            ];
                                            callbacks.modifyOrder({
                                                documents: newDocs,
                                            });
                                        } else {
                                            let newDocs = [...(order?.documents?.length ? order.documents : [])];
                                            const idxToRemove = newDocs.findIndex(
                                                (d) => d.document_id === doc.document_id
                                            );
                                            newDocs.splice(idxToRemove, 1);
                                            callbacks.modifyOrder({
                                                documents: newDocs,
                                            });
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <Typography>{doc.name}</Typography>
                            </Grid>
                            <Grid
                                container
                                xs={2}
                                css={css`
                                    justify-content: space-between;
                                `}
                            >
                                <Grid item>
                                    <Typography>{doc.type}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                ))}
            </Grid>
        </>
    );
}

export default AdditionalDocuments;
